































































































import { DataTableHeader } from 'vuetify';
import { EStreamerType } from '@/api/schema';

import Tooltip from '@/components/tooltip/Tooltip.vue';
import { addThumbnailPrefix } from '@/helpers/copyStakeHelpers';
import { truncateString } from '@/helpers/formatString';
import {
  TAiStreamer,
  TAiStreamerListItem,
  TIMEZONES,
  EAiStreamerStatus
} from '@/views/CopyStake/CopyStakeAiStreamer/types';
import formatDate from '@/helpers/formatDate';
import { LANGUAGES } from '@/helpers/langHelpers';
import defaultAvatar from '@/assets/images/ai-streamer-avatar.png';
import { DateTime } from 'luxon';

const CopyAddress = (): Promise<any> =>
  import('@/components/CopyAddress.vue');
const VStatus = (): Promise<any> => import('@/components/VStatus.vue');

const DEFAULT_PAGINATION_OPTIONS: { page: number; itemsPerPage: number } = {
  page: 1,
  itemsPerPage: 10
};

const STREAMER_TYPE_MAP = {
  [EStreamerType.PRIVATE]: {
    label: 'Private',
    type: 'system-success'
  },
  [EStreamerType.PUBLIC]: {
    label: 'Public',
    type: 'additional-finished'
  }
};

export default {
  name: 'CopyStakeAiStreamersTable',

  props: {
    aiStreamersList: {
      type: Array,
      default: (): any => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    supportedGames: {
      type: Array,
      default: (): any => []
    }
  },
  components: { CopyAddress, Tooltip, VStatus },
  data(): any {
    return {
      LANGUAGES,
      STREAMER_TYPE_MAP,
      EAiStreamerStatus,
      defaultAvatar,
      tableOptions: { ...DEFAULT_PAGINATION_OPTIONS }
    };
  },
  computed: {
    timezonesMap(): Record<string, string> {
      return TIMEZONES.reduce((all, curr) => {
        all[curr.value] = curr.text.split(' ')[0];

        return all;
      }, {});
    },
    aiStreamersFormattedList(): any[] {
      return this.aiStreamersList
        .map((item: TAiStreamerListItem) => {
          return {
            ...item,
            originalItem: item,
            games: this.formatSupportedGames(item.supportedGameIds),
            startTime: item.startTime
              ? this.convertDate(item.startTime, item.zone)
              : '',
            endTime: item.endTime
              ? this.convertDate(item.endTime, item.zone)
              : '',
            actions: this.getActionsBtnParams(item)
          };
        })
        .sort((a: TAiStreamerListItem, b: TAiStreamerListItem) => {
          const dateA = a.dateAdded || '';
          const dateB = b.dateAdded || '';
          return dateB.localeCompare(dateA);
        });
    },
    tableHeaders(): DataTableHeader[] {
      return [
        { text: 'Nickname', value: 'nickname' },
        { text: 'Date Added', value: 'dateAdded' },
        { text: 'Language', value: 'voiceLanguage' },
        { text: 'Type', value: 'streamerType' },
        { text: 'Operator User ID', value: 'operatorUserId' },
        { text: 'Stream Link', value: 'streamLinkId' },
        { text: 'Start/End Time', value: 'startTime' },
        { text: 'Status', value: 'status' },
        { text: 'Action', value: 'actions', sortable: false, width: 300 }
      ];
    },
    statusMap(): any {
      return {
        [EAiStreamerStatus.LIVE]: {
          text: 'Live',
          color: '#26AD60'
        },
        [EAiStreamerStatus.OFFLINE]: {
          text: 'Offline'
        },
        [EAiStreamerStatus.SCHEDULED]: {
          text: 'Scheduled',
          color: '#FF9000'
        },
        [EAiStreamerStatus.STARTING]: {
          text: 'Starting',
          color: '#00AAFF',
          icon: require('@/assets/images/icons/circle-loading.svg')
        }
      };
    }
  },
  methods: {
    truncateString,
    addThumbnailPrefix,
    customSort(
      items: any[],
      sortBy: string[],
      sortDesc: boolean[],
      locale: string
    ): any[] {
      const sorted = [...items];

      const STATUS_PRIORITY: Record<string, number> = {
        [EAiStreamerStatus.LIVE]: 0,
        [EAiStreamerStatus.STARTING]: 1,
        [EAiStreamerStatus.SCHEDULED]: 2,
        [EAiStreamerStatus.OFFLINE]: 3
      };

      const isSortingByStatus = sortBy.length > 0 && sortBy[0] === 'status';

      const getTimeValue = (item: any): number => {
        const rawDate = item.originalItem?.startTime || '';
        const zone = item.originalItem?.zone || 'UTC';

        if (!rawDate) return 0;

        const dt = DateTime.fromFormat(rawDate, 'yyyy-LL-dd HH:mm:ss', {
          zone
        });

        return dt.isValid ? dt.toMillis() : 0;
      };

      sorted.sort((a, b) => {
        const priorityA = STATUS_PRIORITY[a.status] ?? 99;
        const priorityB = STATUS_PRIORITY[b.status] ?? 99;

        if (priorityA !== priorityB) {
          if (isSortingByStatus) {
            const desc = sortDesc[0] ? -1 : 1;
            return (priorityA - priorityB) * desc;
          }

          return priorityA - priorityB;
        }

        if (isSortingByStatus) {
          const timeA = getTimeValue(a);
          const timeB = getTimeValue(b);

          if (timeA !== timeB) {
            return timeA - timeB;
          }
        }

        if (sortBy.length) {
          for (let i = 0; i < sortBy.length; i++) {
            const key = sortBy[i];
            if (key === 'status') continue;

            const desc = sortDesc[i] ? -1 : 1;

            if (key === 'startTime') {
              const timeA = getTimeValue(a);
              const timeB = getTimeValue(b);

              if (timeA === timeB) continue;
              return (timeA - timeB) * desc;
            }

            const valA = a[key];
            const valB = b[key];

            if (valA === valB) continue;
            if (valA == null) return 1;
            if (valB == null) return -1;

            if (typeof valA === 'string') {
              const cmp = valA.localeCompare(valB, locale);
              if (cmp !== 0) return cmp * desc;
            } else {
              if (valA < valB) return -1 * desc;
              if (valA > valB) return 1 * desc;
            }
          }
        }

        return 0;
      });

      return sorted;
    },
    getActionsBtnParams(item: TAiStreamerListItem): any {
      const manageStreamActionBtnParams = {
        [EAiStreamerStatus.STARTING]: {
          text: 'Stop Stream',
          action: () => {
            this.$emit('stop-stream-dialog', item);
          },
          icon: 'mdi-stop-circle-outline',
          color: 'error',
          isDisabled: false
        },
        [EAiStreamerStatus.LIVE]: {
          text: 'Stop Stream',
          action: () => {
            this.$emit('stop-stream-dialog', item);
          },
          icon: 'mdi-stop-circle-outline',
          color: 'error',
          isDisabled: false
        },
        [EAiStreamerStatus.SCHEDULED]: {
          text: 'Cancel Stream',
          action: () => {
            this.$emit('cancel-stream-dialog', item);
          },
          icon: 'mdi-stop-circle-outline',
          color: '#FF9000',
          isDisabled: false
        },
        [EAiStreamerStatus.OFFLINE]: {
          text: 'Plan Stream',
          action: () => {
            this.$emit('open-plan-stream-dialog', item);
          },
          icon: 'mdi-play-circle-outline',
          color: '#12B76A',
          isDisabled: false
        }
      };
      return {
        manageStream: manageStreamActionBtnParams[item.status],
        edit: {
          tooltipText:
            item.status === EAiStreamerStatus.LIVE ||
            item.status === EAiStreamerStatus.STARTING
              ? 'Stop the stream for editing'
              : null,
          isDisabled:
            item.status === EAiStreamerStatus.LIVE ||
            item.status === EAiStreamerStatus.STARTING
        },
        delete: {
          tooltipText:
            item.status === EAiStreamerStatus.LIVE ||
            item.status === EAiStreamerStatus.STARTING
              ? 'Stop the stream before deleting'
              : null,
          isDisabled:
            item.status === EAiStreamerStatus.LIVE ||
            item.status === EAiStreamerStatus.STARTING
        }
      };
    },
    formatToAiStreamerItem(item: TAiStreamerListItem): Partial<TAiStreamer> {
      return {
        id: item.id,
        supportedGameIds: item.supportedGameIds,
        startTime: item.startTime,
        endTime: item.endTime,
        zone: item.zone,
        streamerId: item.streamerId,
        defaultBetAmount: item.defaultBetAmount,
        minBalanceAmount: item.minBalanceAmount as number,
        currency: item.currency,
        scenarioTemplateId: item.scenarioTemplateId,
        avatarId: item.avatarId,
        voiceLanguage: item.voiceLanguage,
        baseScriptId: item.baseScriptId,
        voiceId: item.voiceId,
        avatarService: item.avatarService
      };
    },
    handleEditItem(item: TAiStreamerListItem): void {
      this.$emit(
        'open-edit-ai-streamer-dialog',
        this.formatToAiStreamerItem(item)
      );
    },
    handleCopyItem(item: TAiStreamerListItem): void {
      this.$emit(
        'open-copy-ai-streamer-dialog',
        this.formatToAiStreamerItem(item)
      );
    },
    handleDeleteConfig(item: TAiStreamerListItem): void {
      this.$emit('delete-ai-streamer-dialog', item);
    },
    handleClickManageStreamBtn(action: () => void): void {
      if (typeof action === 'function') {
        try {
          action();
        } catch (e) {
          console.error(e);
        }
      }
    },
    convertDate(
      date: string,
      zone: string
    ): {
      value: string;
      zone: string;
    } {
      return {
        value: formatDate(date, 'yyyy-MM-dd HH:mm', false, false),
        zone: this.timezonesMap[zone] ? `${this.timezonesMap[zone]}` : ''
      };
    },
    formatSupportedGames(games: number[]): {
      list: string[];
      suffix?: string;
    } {
      const max = 3;
      const list = games
        .map((gameId) => {
          const game = this.supportedGames.find(({ id }) => id === gameId);

          return game?.gameName;
        })
        .filter(Boolean);
      const total = list.length;

      return {
        list: [...list].slice(0, max),
        suffix: total > max ? `+${total - max} others` : undefined
      };
    }
  }
};
